import { Document, Page, pdfjs } from "react-pdf";
import { useState, useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function DocumentPreview({
  docUrl,
  docLoading,
  setDocLoading,
}: {
  docUrl: string;
  docLoading: boolean;
  setDocLoading: Function;
}) {
  const [numPages, setNumPages] = useState<number>(0);
  // Fit the page to the viewport (never wider than the container's max), and
  // initialise from the real window width so the very first render is correct
  // on narrow phones — previously the page rendered at a fixed 360px until a
  // resize event fired, which zoomed/cut the document on small screens.
  const computeWidth = () =>
    typeof window !== "undefined"
      ? Math.min(window.innerWidth - 48, 340)
      : 320;
  const [pageWidth, setPageWidth] = useState<number>(computeWidth);

  // Loader shown inside the white document card, so it uses the brand color
  // on white rather than white-on-orange.
  const loaderContent = (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        py: 6,
        px: 4,
      }}
    >
      <CircularProgress sx={{ color: "#EC5560" }} aria-hidden="true" />
      <Typography
        sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.95rem" }}
      >
        Loading document…
      </Typography>
    </Box>
  );

  useEffect(() => {
    const handleResize = () => {
      setPageWidth(computeWidth());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        overflowX: "hidden",
      }}
    >
      {/* Document card is shown from the first render: it holds the loader
          while the URL/PDF is being fetched, then swaps to the document. */}
      <Box
        sx={{
          width: pageWidth,
          maxWidth: "100%",
          margin: "0 auto",
          mb: 2,
          borderRadius: "12px",
          border: "2px solid #EC5560",
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          backgroundColor: "#ffffff",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight:
            !docUrl || docLoading ? Math.round(pageWidth * 1.29) : undefined,
        }}
      >
        {docUrl ? (
          <Document
            file={docUrl}
            onLoadSuccess={({ numPages }) => {
              setNumPages(numPages);
              setDocLoading(false);
            }}
            loading={loaderContent}
            error={loaderContent}
            renderMode="canvas"
          >
            <Page
              pageNumber={1}
              width={pageWidth}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              canvasRef={(node: HTMLCanvasElement | null) => {
                if (node) {
                  node.setAttribute("role", "img");
                  node.setAttribute("aria-label", "Document preview, page 1");
                }
              }}
            />
          </Document>
        ) : (
          loaderContent
        )}
      </Box>
    </Box>
  );
}
